import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { PageEvent } from '@angular/material/paginator';
import { Subject, forkJoin } from 'rxjs';
import { distinctUntilChanged, skip, takeUntil } from 'rxjs/operators';
import moment from 'moment';
import { TranslateService } from '@ngx-translate/core';
import { DomainFilterService } from 'app/services/domain-filter.service';
import { AssistantUsageService } from './assistant-usage.service';
import { AssistantUsageActionStatus, IAssistantUsageLog, IAssistantUsageLogs, IAssistantUsageQuery, IAssistantUsageSummary } from './assistant-usage.models';

type PeriodPreset = '7d' | '30d' | 'month' | 'previous_month' | 'custom';

const ACTION_STATUS_LABELS: Record<AssistantUsageActionStatus, string> = {
	confirmed: 'Applicata',
	canceled: 'Annullata',
	failed: 'Non riuscita',
	expired: 'Scaduta',
	pending: 'In attesa'
};

const SCREEN_LABELS: Record<string, string> = {
	trip_packages: 'Pacchetto',
	trip_services: 'Criterio'
};

/**
 * Consumi dell'assistente AI del dominio selezionato: budget live, totali, andamento, utenti, attività.
 * Solo amministratori del dominio (voce di menu e 403 lato server).
 */
@Component({
	selector: 'assistant-usage-page',
	templateUrl: './assistant-usage.component.html',
	styleUrls: ['./assistant-usage.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false
})
export class AssistantUsagePageComponent implements OnInit, OnDestroy{

	readonly presets: { value: PeriodPreset; label: string }[] = [
		{ value: '7d', label: 'Ultimi 7 giorni' },
		{ value: '30d', label: 'Ultimi 30 giorni' },
		{ value: 'month', label: 'Questo mese' },
		{ value: 'previous_month', label: 'Mese scorso' }
	];
	readonly timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Rome';
	readonly range = new FormGroup({
		from: new FormControl<moment.Moment | null>(null),
		to: new FormControl<moment.Moment | null>(null)
	});
	readonly userFilter = new FormControl<string | null>(null);

	preset: PeriodPreset = '30d';
	summary: IAssistantUsageSummary | null = null;
	logs: IAssistantUsageLogs | null = null;
	loading = false;
	loadingLogs = false;
	error: { code: string; message: string } | null = null;
	page = 1;
	readonly perPage = 20;

	private readonly destroyed = new Subject<void>();

	constructor(
		private usageService: AssistantUsageService,
		private domainFilterService: DomainFilterService,
		private translate: TranslateService
	){}

	/** lingua dell'interfaccia per date e numeri (moment non ha locale caricati) */
	private get locale(): string{
		return this.translate.currentLang === 'en' ? 'en-GB' : 'it-IT';
	}

	private date(value: moment.Moment | string, options: Intl.DateTimeFormatOptions): string{
		return new Intl.DateTimeFormat(this.locale, options).format(moment(value).toDate());
	}

	ngOnInit(): void{
		this.applyPreset('30d', false);
		this.load();
		this.domainFilterService.filterDomainId.pipe(skip(1), distinctUntilChanged(), takeUntil(this.destroyed)).subscribe(() => {
			this.userFilter.setValue(null, { emitEvent: false });
			this.load();
		});
		this.userFilter.valueChanges.pipe(takeUntil(this.destroyed)).subscribe(() => {
			this.page = 1;
			this.loadLogs();
		});
	}

	ngOnDestroy(): void{
		this.destroyed.next();
		this.destroyed.complete();
	}

	applyPreset(preset: PeriodPreset, reload = true): void{
		this.preset = preset;
		const today = moment().startOf('day');
		const ranges: Record<Exclude<PeriodPreset, 'custom'>, [moment.Moment, moment.Moment]> = {
			'7d': [today.clone().subtract(6, 'days'), today],
			'30d': [today.clone().subtract(29, 'days'), today],
			month: [today.clone().startOf('month'), today],
			previous_month: [today.clone().subtract(1, 'month').startOf('month'), today.clone().subtract(1, 'month').endOf('month').startOf('day')]
		};
		if (preset !== 'custom') {
			const [from, to] = ranges[preset];
			this.range.setValue({ from, to }, { emitEvent: false });
		}
		if (reload) this.load();
	}

	/** chiusura del calendario: ricarica solo con entrambe le date */
	onRangeClosed(): void{
		const { from, to } = this.range.value;
		if (!from || !to) return;
		this.preset = 'custom';
		this.load();
	}

	load(): void{
		this.loading = true;
		this.error = null;
		this.page = 1;
		forkJoin({ summary: this.usageService.summary(this.query()), logs: this.usageService.logs(this.logsQuery()) }).subscribe({
			next: ({ summary, logs }) => {
				this.summary = summary;
				this.logs = logs;
				this.loading = false;
			},
			error: (response: HttpErrorResponse) => this.fail(response)
		});
	}

	loadLogs(): void{
		this.loadingLogs = true;
		this.usageService.logs(this.logsQuery()).subscribe({
			next: logs => {
				this.logs = logs;
				this.loadingLogs = false;
			},
			error: (response: HttpErrorResponse) => {
				this.loadingLogs = false;
				this.fail(response);
			}
		});
	}

	onPage(event: PageEvent): void{
		this.page = event.pageIndex + 1;
		this.loadLogs();
	}

	// --- budget ---

	/** importi in USD solo agli admin di piattaforma: gli admin del dominio vedono token e quota del budget */
	get showCosts(): boolean{
		return !!this.summary?.show_costs;
	}

	get budgetRatio(): number{
		return Math.min(1, this.summary?.budget?.used_ratio ?? 0);
	}

	/** token ancora disponibili nel periodo */
	get budgetLeft(): number{
		const budget = this.summary?.budget;
		return budget ? Math.max(0, budget.max_tokens - budget.used_tokens) : 0;
	}

	percent(ratio: number): string{
		return new Intl.NumberFormat(this.locale, { style: 'percent', maximumFractionDigits: ratio > 0 && ratio < .01 ? 1 : 0 }).format(ratio);
	}

	get averageTokens(): number{
		const totals = this.summary?.totals;
		return totals?.requests ? Math.round(totals.total_tokens / totals.requests) : 0;
	}

	get budgetTone(): 'calm' | 'warn' | 'over'{
		if (this.budgetRatio >= 1) return 'over';
		return this.budgetRatio >= .8 ? 'warn' : 'calm';
	}

	get resetLabel(): string | null{
		const resetAt = this.summary?.budget?.budget_reset_at;
		if (!resetAt) return null;
		const reset = moment(resetAt);
		const days = reset.clone().startOf('day').diff(moment().startOf('day'), 'days');
		const date = this.date(reset, { day: 'numeric', month: 'long' });
		if (days <= 0) return this.translate.instant('Si azzera oggi');
		if (days === 1) return this.translate.instant('Si azzera domani, {{date}}', { date });
		return this.translate.instant('Si azzera il {{date}}, tra {{days}} giorni', { date, days });
	}

	// --- andamento ---

	get maxDayTokens(): number{
		return Math.max(1, ...(this.summary?.by_day ?? []).map(day => day.total_tokens));
	}

	barHeight(tokens: number): string{
		if (!tokens) return '0';
		return Math.max(4, Math.round(tokens / this.maxDayTokens * 100)) + '%';
	}

	dayTooltip(day: { date: string; requests: number; total_tokens: number; cost_usd: number }): string{
		const date = this.date(day.date, { weekday: 'short', day: 'numeric', month: 'short' });
		if (!day.requests) return this.translate.instant('{{date}}: nessuna richiesta', { date });
		const text = this.translate.instant(day.requests === 1 ? '{{date}}: 1 richiesta, {{tokens}} token' : '{{date}}: {{requests}} richieste, {{tokens}} token', {
			date, requests: day.requests, tokens: this.tokens(day.total_tokens)
		});
		return this.showCosts && day.cost_usd !== undefined ? text + ', ' + this.money(day.cost_usd) : text;
	}

	/** etichette sotto il grafico: inizio, metà, fine (con più di 14 giorni le etichette giornaliere non stanno) */
	axisLabel(index: number, count: number, date: string): string | null{
		if (count <= 14 || index === 0 || index === count - 1 || index === Math.floor((count - 1) / 2)) {
			return this.date(date, count <= 14 ? { day: 'numeric' } : { day: 'numeric', month: 'short' });
		}
		return null;
	}

	get hasDailyActivity(): boolean{
		return (this.summary?.by_day ?? []).some(day => day.total_tokens > 0);
	}

	// --- utenti e attività ---

	userShare(tokens: number): string{
		const total = this.summary?.totals.total_tokens || 1;
		return Math.round(tokens / total * 100) + '%';
	}

	actionLabel(status: AssistantUsageActionStatus): string{
		return ACTION_STATUS_LABELS[status] ?? status;
	}

	whereLabel(log: IAssistantUsageLog): string | null{
		if (log.entity) return SCREEN_LABELS[log.entity.type] ?? log.entity.type;
		return log.route ? 'Altra schermata' : null;
	}

	/** link solo se l'entità esiste ancora (title presente) */
	entityLink(log: IAssistantUsageLog): string[] | null{
		return log.entity?.title ? ['/dataset/detail', log.entity.type, log.entity.id] : null;
	}

	initials(name: string): string{
		return name.split(/\s+/).filter(Boolean).slice(0, 2).map(part => part[0].toUpperCase()).join('') || '?';
	}

	// --- formati ---

	tokens(value: number): string{
		return new Intl.NumberFormat(this.locale).format(value);
	}

	compactTokens(value: number): string{
		return new Intl.NumberFormat(this.locale, { notation: 'compact', maximumFractionDigits: 1 }).format(value);
	}

	/** USD: centesimi per gli importi normali, 4 decimali per quelli di una singola richiesta */
	money(value: number | undefined): string{
		value = value ?? 0;
		const digits = value > 0 && value < .01 ? 4 : 2;
		return new Intl.NumberFormat(this.locale, { style: 'currency', currency: 'USD', minimumFractionDigits: digits, maximumFractionDigits: digits }).format(value);
	}

	when(iso: string): string{
		const date = moment(iso);
		const days = moment().startOf('day').diff(date.clone().startOf('day'), 'days');
		const time = this.date(date, { hour: '2-digit', minute: '2-digit' });
		if (days === 0) return this.translate.instant('Oggi, {{time}}', { time });
		if (days === 1) return this.translate.instant('Ieri, {{time}}', { time });
		return this.date(date, { day: 'numeric', month: 'short', year: 'numeric' }) + ', ' + time;
	}

	get periodLabel(): string{
		if (!this.summary) return '';
		const from = moment(this.summary.period.from);
		const to = moment(this.summary.period.to);
		const sameYear = from.year() === to.year();
		return this.date(from, sameYear ? { day: 'numeric', month: 'long' } : { day: 'numeric', month: 'long', year: 'numeric' })
			+ ' – ' + this.date(to, { day: 'numeric', month: 'long', year: 'numeric' });
	}

	trackLog(_index: number, log: IAssistantUsageLog): number{
		return log.id;
	}

	private query(): IAssistantUsageQuery{
		const { from, to } = this.range.value;
		return {
			from: (from ?? moment().subtract(29, 'days')).format('YYYY-MM-DD'),
			to: (to ?? moment()).format('YYYY-MM-DD'),
			timezone: this.timezone
		};
	}

	private logsQuery(): IAssistantUsageQuery{
		return { ...this.query(), user_id: this.userFilter.value, page: this.page, per_page: this.perPage };
	}

	private fail(response: HttpErrorResponse): void{
		this.loading = false;
		this.loadingLogs = false;
		const serverError = response.error?.error;
		if (response.status === 403 || response.status === 422) {
			this.error = { code: serverError?.code ?? 'forbidden', message: serverError?.message ?? 'Non hai accesso ai consumi di questa azienda.' };
		} else {
			this.error = { code: 'unavailable', message: 'I consumi non sono stati caricati: controlla la connessione e riprova.' };
		}
	}
}
